import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Html5Qrcode } from 'html5-qrcode';
import '../../../scss/MaterialRequestScanner.scss';
import routes from '../../../Routes/routes';
import { errorMsg } from '../../../Apis/functions';
import { DecryptMessage } from '../../../reduxToolkit/EnDecrypt/EnDecryptApi';
import {
    QR_CODE_TYPE,
    SCANNER_CONFIG,
    SCANNER_REGION_ID,
    SCANNER_STATUS,
    SCANNER_STATUS_ACTIONS,
    UUID_REGEX,
} from '../Model/Model';

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

/**
 * Reads the encrypted material request identifier out of a scanned QR code.
 *
 * The QR body is the one built by the backend (QRCodeBody), so the identifier
 * travels encrypted inside the `data` field and only a material request type is
 * accepted here.
 *
 * @param {string} decodedText Raw text read from the camera.
 * @returns {string|null} Encrypted identifier, or null when the QR is not a material request one.
 */
const readMaterialRequestData = (decodedText) => {
    try {
        const qrBody = JSON.parse(decodedText);

        if (qrBody?.type !== QR_CODE_TYPE.MATERIAL_REQUEST || !qrBody?.data) {
            return null;
        }

        return qrBody.data;
    } catch (error) {
        return null;
    }
};

export const MaterialRequestScannerApp = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [status, setStatus] = useState(SCANNER_STATUS.IDLE);
    const [showSpinner, setShowSpinner] = useState(false);
    // The camera instance lives in a ref because the html5-qrcode callbacks are
    // registered once and would otherwise read a stale state value.
    const scannerRef = useRef(null);
    // A single QR can be decoded several times per second, so the read is locked
    // while the identifier is being resolved.
    const processingRef = useRef(false);

    /**
     * Stops the camera and releases the preview.
     */
    const stopScan = useCallback(async () => {
        const scanner = scannerRef.current;
        scannerRef.current = null;

        if (!scanner) {
            return;
        }

        try {
            await scanner.stop();
            scanner.clear();
        } catch (error) {
            // The camera was already stopped, nothing else to release.
        }
    }, []);

    /**
     * Resolves a scanned QR code and redirects to the material request details.
     *
     * @param {string} decodedText Raw text read from the camera.
     */
    const onScanSuccess = async (decodedText) => {
        if (processingRef.current) {
            return;
        }

        processingRef.current = true;
        const encryptedId = readMaterialRequestData(decodedText);

        if (!encryptedId) {
            processingRef.current = false;
            toast.error(t('invalid_qr_code'));
            return;
        }

        setStatus(SCANNER_STATUS.RESOLVING);
        setShowSpinner(true);
        await stopScan();

        dispatch(DecryptMessage(encryptedId)).then(({ payload }) => {
            setShowSpinner(false);
            setStatus(SCANNER_STATUS.IDLE);
            processingRef.current = false;
            const { data, success, code } = payload?.data || {};

            if (success !== true) {
                errorMsg(code);
                return;
            }

            const id = typeof data === 'string' ? data.trim() : '';

            if (!UUID_REGEX.test(id)) {
                toast.error(t('invalid_qr_code'));
                return;
            }

            navigate(`${routes.materialRequest.dispatch}${id}`, { state: { from: routes.materialRequest.scanner } });
        });
    };

    /**
     * Starts the camera and begins looking for a QR code.
     */
    const startScan = async () => {
        if (scannerRef.current) {
            return;
        }

        try {
            const scanner = new Html5Qrcode(SCANNER_REGION_ID);
            scannerRef.current = scanner;
            await scanner.start({ facingMode: 'environment' }, SCANNER_CONFIG, onScanSuccess, undefined);

            // The module can be left while the camera is still starting, in that
            // case the reference was already released and the camera has to stop.
            if (scannerRef.current !== scanner) {
                await scanner.stop();
                scanner.clear();
                return;
            }

            setStatus(SCANNER_STATUS.SCANNING);
        } catch (error) {
            scannerRef.current = null;
            setStatus(SCANNER_STATUS.BLOCKED);
            toast.info(t('please_grant_permissions_to_use_the_camera'));
        }
    };

    /**
     * Get current button/status configuration based on the active status.
     */
    const currentAction = SCANNER_STATUS_ACTIONS[status];

    // The camera starts on its own so the guard only points the device to the QR,
    // and it is released when the user leaves the module.
    useEffect(() => {
        startScan();

        return () => {
            stopScan();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        t,
        status,
        currentAction,
        showSpinner,
        // The scanner is always opened from the material request list, and going back
        // through the history would return to the dispatch view after a scan.
        goBack: () => navigate(routes.materialRequest.main),
    };
};
